import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import '../../App.css';

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canViewStats = hasPermission('viewAdminStats');

  useEffect(() => {
    if (canViewStats) {
      fetchStats();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewStats]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/management/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError(err.response?.data?.error || 'Failed to load admin statistics');
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container"><p>Loading admin dashboard...</p></div>;
  }

  if (error) {
    return <div className="container"><p className="error">{error}</p></div>;
  }

  return (
    <div className="container">
      <h1>Admin Dashboard</h1>
      
      {hasPermission('viewAdminStats') && stats && (
        <div className="admin-stats">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Pending Users</h3>
              <p className="stat-number pending">{stats.users?.pending || 0}</p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/admin/users')}
              >
                Manage Users
              </button>
            </div>

            <div className="stat-card">
              <h3>Approved Users</h3>
              <p className="stat-number approved">{stats.users?.approved || 0}</p>
            </div>

            <div className="stat-card">
              <h3>Rejected Users</h3>
              <p className="stat-number rejected">{stats.users?.rejected || 0}</p>
            </div>

            <div className="stat-card">
              <h3>Total Users</h3>
              <p className="stat-number">{stats.users?.total || 0}</p>
            </div>

            <div className="stat-card">
              <h3>Pending Dependent Requests</h3>
              <p className="stat-number pending">{stats.dependentRequests?.pending || 0}</p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/admin/dependent-requests')}
              >
                Review Requests
              </button>
            </div>

            <div className="stat-card">
              <h3>Active Codes</h3>
              <p className="stat-number">{stats.registrationCodes?.active || 0}</p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/admin/registration-codes')}
              >
                Manage Codes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          {hasPermission('approveUsers') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/notifications')}
            >
              🔔 Notifications
            </button>
          )}
          {hasPermission('approveUsers') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/compose-email')}
            >
              ✉️ Compose Email
            </button>
          )}
          {hasPermission('approveUsers') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/users')}
            >
              User Management
            </button>
          )}
          {hasPermission('approveUsers') && (
            <button 
              className="btn btn-success"
              onClick={() => navigate('/admin/create-user')}
            >
              ➕ Create User
            </button>
          )}
          {hasPermission('approveUsers') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/manage-user-picks')}
            >
              📝 Manage User Picks
            </button>
          )}
          {hasPermission('approveUsers') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/dependent-requests')}
            >
              Dependent Requests
            </button>
          )}
          {hasPermission('approveUsers') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/manage-dependents')}
            >
              Manage Dependents
            </button>
          )}
          {hasPermission('manageRegistrationCodes') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/registration-codes')}
            >
              Registration Codes
            </button>
          )}
          {hasPermission('manageTidbits') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/tidbits')}
            >
              Manage Tidbits
            </button>
          )}
          {hasPermission('manageRaces') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/races')}
            >
              Manage Races
            </button>
          )}
          {hasPermission('manageHorseRacing') && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/horse-racing')}
            >
              Horse Racing API
            </button>
          )}
        </div>
      </div>

      <style jsx="true">{`
        .admin-stats {
          margin: 2rem 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .stat-card h3 {
          margin-top: 0;
          color: #555;
          font-size: 1rem;
          font-weight: 500;
        }

        .stat-number {
          font-size: 3rem;
          font-weight: bold;
          margin: 1rem 0;
          color: #333;
        }

        .stat-number.pending {
          color: #ff9800;
        }

        .stat-number.approved {
          color: #4caf50;
        }

        .stat-number.rejected {
          color: #f44336;
        }

        .admin-actions {
          margin-top: 3rem;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background-color: #0056b3;
        }
      `}</style>
    </div>
  );
}

export default AdminDashboard;
